import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router';
import { JobSiteSkyline } from '@/components/brand/JobSiteSkyline';
import { LegalLink } from '@/components/LegalLink';
import { SignInDemo } from '@/components/brand/SignInDemo';
import { Logo } from '@/components/Logo';
import { ThemeToggleButton } from '@/components/ThemeToggle';
import { useAuth } from '@/lib/auth';
import { useBranding } from '@/lib/branding';
import { openCookieSettings } from '@/lib/consent';
import { useMediaQuery } from '@/lib/hooks';
import { STATUS_PAGE_URL, useServiceStatus, type ServiceState } from '@/lib/serviceStatus';
import { clearTenantSlug, hasStoredTenantSlug, tenantSlugFromHost } from '@/lib/tenant';
import { cn } from '@/lib/utils';

/**
 * Shell for sign-in, invitation, password and help pages.
 *
 * Follows the active theme: a sand/neutral canvas in light mode, the tenant's deep surface in dark
 * mode. The brand is carried by the logo, the accent hairlines and the animated job-site skyline.
 *
 * Built to fit one viewport on a laptop: a quiet masthead (logo, help, theme), a compact serif
 * headline, the form card centred in the room between the masthead and the horizon band, then a
 * thin footer bar. On short viewports (`short:` variant, ≤940px) the intro line hides, the headline
 * shrinks and the horizon band gets lower so the whole page still fits. On xl screens the card
 * is flanked by the animated product demo (components/brand/SignInDemo): a worker's phone on the
 * left and a miniature compose console on the right, both tilted in 3D, playing a looping story of
 * a notice being sent, delivered and answered with a keyword. Decorative pieces are aria-hidden
 * and hidden below xl; a tenant may switch the demo and the skyline off in its sign-in branding.
 */
export function AuthLayout({
  title,
  eyebrow,
  description,
  children,
  footer,
  tenantLine,
  demo = true,
  wide = false,
}: {
  title: string;
  eyebrow?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** Small line under the card; defaults to the sign-in tenant confirmation. */
  tenantLine?: ReactNode;
  /** Show the animated product demo beside the card (sign-in only; forms like Help turn it off). */
  demo?: boolean;
  /** A wider card for longer forms. */
  wide?: boolean;
}) {
  const { branding } = useBranding();
  const { status } = useAuth();
  const tagline = branding.tagline?.trim() || 'Reach everyone on the job.';
  const signIn = branding.signIn ?? { title: 'Sign in', intro: '', footerNote: '', showNotes: true, showSkyline: true };
  const poweredBy = !/crewline/i.test(branding.appName);
  // On a sign-in domain the host itself names the tenant: nothing to confirm, and "switch" would only land back here.
  const hostLinked = tenantSlugFromHost() !== null;
  const canSwitchTenant = !hostLinked && hasStoredTenantSlug();
  const wideScreen = useMediaQuery('(min-width: 640px)');
  const homeHref = status === 'authenticated' ? '/' : '/login';
  const showDemo = demo && signIn.showNotes;

  return (
    <div className="bg-muted text-foreground dark:bg-background flex min-h-dvh flex-col">
      <div className="bg-brand-accent h-0.5 shrink-0" aria-hidden />

      <header className="short:h-14 mx-auto flex h-14 w-full max-w-[1280px] shrink-0 items-center justify-between px-5 sm:h-16 sm:px-10">
        <Link to={homeHref} className="flex items-center outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50" aria-label={status === 'authenticated' ? `${branding.appName} home` : `${branding.appName} sign in`}>
          <Logo size="md" className="sm:hidden" />
          <Logo size="lg" className="hidden sm:block" />
        </Link>
        <nav className="flex items-center justify-end gap-4 text-sm sm:gap-6">
          <Link to="/help" className="text-muted-foreground hover:text-foreground underline-offset-4 transition-colors hover:underline">
            Help
          </Link>
          <ThemeToggleButton />
        </nav>
      </header>

      <main className="relative flex w-full flex-1 flex-col overflow-hidden">
        {/* Content sits centred in the room between the masthead and the horizon band. */}
        <div className="short:py-3 mx-auto flex w-full max-w-[1280px] flex-1 flex-col items-center justify-center px-5 py-5 sm:px-10">
          <div className="max-w-3xl text-center">
            <p className="text-muted-foreground flex items-center justify-center gap-3 text-[11px] font-medium tracking-[0.18em] uppercase">
              <span className="bg-brand-accent size-1.5" aria-hidden />
              {branding.appName}
              <span className="bg-brand-accent size-1.5" aria-hidden />
            </p>
            <h2 className="font-display short:text-[2.5rem] mt-3 text-[clamp(2.25rem,4.6vw,3.75rem)] leading-[0.95] tracking-[-0.02em] text-balance">
              <Headline text={tagline} />
            </h2>
            {signIn.intro ? <p className="short:hidden text-muted-foreground mx-auto mt-3 max-w-lg text-sm leading-relaxed text-balance">{signIn.intro}</p> : null}
          </div>

          {/* Card between the two demo stages (columns 1 and 3); on smaller screens only the card renders. */}
          <div className={cn('short:mt-5 mt-7 w-full', showDemo && 'xl:grid xl:grid-cols-[300px_minmax(0,480px)_300px] xl:items-start xl:justify-center xl:gap-x-14 xl:[perspective:1600px]')}>
            {showDemo ? <SignInDemo /> : null}

            <div className="flex flex-col items-center xl:col-start-2 xl:row-start-1">
              <section
                aria-labelledby="auth-title"
                className={cn(
                  'bg-card text-card-foreground border-t-brand-accent relative w-full border border-t-2',
                  wide ? 'max-w-[720px]' : 'max-w-[480px]',
                  'motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-3 duration-500',
                  // Auth forms get taller controls than the rest of the app.
                  '[&_input[data-slot=input]]:h-11 [&_button[type=submit]]:h-11 [&_button[type=submit]]:text-[15px] [&_form]:gap-5',
                )}
              >
                <div className="short:pt-7 short:pb-4 px-6 pt-7 pb-5 sm:px-10 sm:pt-9 sm:pb-6">
                  {eyebrow && <div className="text-primary mb-3 text-xs font-medium tracking-wide uppercase">{eyebrow}</div>}
                  <h1 id="auth-title" className="font-display text-[2rem] leading-none tracking-tight sm:text-4xl">
                    {title}
                  </h1>
                  {description && <p className="text-muted-foreground mt-3 text-sm leading-relaxed">{description}</p>}
                </div>
                <div className="short:pb-7 px-6 pb-7 sm:px-10 sm:pb-9">{children}</div>
                {footer && <div className="bg-muted/40 text-muted-foreground border-t px-6 py-4 text-sm leading-relaxed sm:px-10">{footer}</div>}
              </section>

              {(tenantLine != null || !hostLinked) && (
              <p className="text-muted-foreground mt-4 text-center text-xs">
                {tenantLine ?? (
                  <>
                    You are signing in to <span className="text-foreground font-medium">{branding.tenantName}</span>.
                  </>
                )}
                {!tenantLine && canSwitchTenant && (
                  <>
                    {' '}
                    <button
                      type="button"
                      className="hover:text-foreground underline underline-offset-4 transition-colors"
                      onClick={() => {
                        clearTenantSlug();
                        window.location.assign('/login');
                      }}
                    >
                      Not your organization?
                    </button>
                  </>
                )}
              </p>
              )}
            </div>
          </div>
        </div>

        {/* Horizon: the animated skyline stands on the footer line (tenant may hide it). */}
        {signIn.showSkyline ? (
          <div className="short:h-[72px] h-[clamp(80px,9vw,128px)] w-full shrink-0" aria-hidden>
            <JobSiteSkyline fit={wideScreen ? 'contain' : 'cover'} />
          </div>
        ) : null}
      </main>

      <footer className="bg-card/70 dark:bg-brand-panel dark:text-brand-panel-foreground dark:border-brand-panel-foreground/15 relative shrink-0 border-t">
        <div className="text-muted-foreground dark:text-brand-panel-foreground/60 mx-auto flex w-full max-w-[1280px] flex-col gap-1.5 px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] text-xs sm:flex-row sm:items-center sm:justify-between sm:px-10">
          <span>
            {poweredBy && ' Powered by Crewline ·'} Trouble signing in?{' '}
            <Link className="hover:text-foreground dark:hover:text-brand-panel-foreground underline underline-offset-4 transition-colors" to="/help">
              Get help
            </Link>
            {/*{branding.supportEmail && (*/}
            {/*  <>*/}
            {/*    {' '}*/}
            {/*    or email{' '}*/}
            {/*    <a className="hover:text-foreground dark:hover:text-brand-panel-foreground underline underline-offset-4 transition-colors" href={`mailto:${branding.supportEmail}`}>*/}
            {/*      {branding.supportEmail}*/}
            {/*    </a>*/}
            {/*  </>*/}
            {/*)}*/}
          </span>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
            <ServiceStatusPill />
            <LegalLink href={branding.legal.privacyUrl} fallback="/legal/privacy" className="hover:text-foreground dark:hover:text-brand-panel-foreground underline-offset-4 transition-colors hover:underline">
              Privacy
            </LegalLink>
            <LegalLink href={branding.legal.termsUrl} fallback="/legal/terms" className="hover:text-foreground dark:hover:text-brand-panel-foreground underline-offset-4 transition-colors hover:underline">
              Terms
            </LegalLink>
            <LegalLink href={branding.legal.signupUrl} fallback="/opt-in" className="hover:text-foreground dark:hover:text-brand-panel-foreground underline-offset-4 transition-colors hover:underline">
              Job-site enrollment
            </LegalLink>
            <button type="button" onClick={openCookieSettings} className="hover:text-foreground dark:hover:text-brand-panel-foreground underline-offset-4 transition-colors hover:underline">
              Cookies
            </button>
            <span>
              {branding.appName}
              {/*{poweredBy && ' · Powered by Crewline'}*/}
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}

/** Tagline with words rising in one after another and the last word set in italic; works for any tenant's copy. */
function Headline({ text }: { text: string }) {
  const words = text.split(/\s+/).filter(Boolean);
  return (
    <>
      {words.map((word, i) => (
        <Fragment key={`${word}-${i}`}>
          <span
            className={cn('motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-2 fill-mode-both inline-block duration-700', i === words.length - 1 && 'italic')}
            style={{ animationDelay: `${80 + i * 90}ms` }}
          >
            {word}
          </span>
          {/* A real space between the inline-blocks; a trailing space inside one is collapsed away. */}
          {i < words.length - 1 ? ' ' : null}
        </Fragment>
      ))}
    </>
  );
}

const STATUS_COPY: Record<ServiceState, string> = {
  operational: 'All systems operational',
  degraded: 'Some services are degraded',
  unreachable: 'Service unreachable',
};

function ServiceStatusPill() {
  const { data } = useServiceStatus();
  if (!data) return null;
  return (
    <a href={STATUS_PAGE_URL} className="hover:text-foreground dark:hover:text-brand-panel-foreground flex items-center gap-2 underline-offset-4 transition-colors hover:underline" role="status" title="Open the system status page">
      <span className={cn('size-1.5', data.state === 'operational' ? 'bg-success' : 'bg-warning')} aria-hidden />
      {STATUS_COPY[data.state]}
    </a>
  );
}
